import { Ionicons } from '@expo/vector-icons';
import React, { useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { HowToPlay, HowToPlayButton, useHowToPlay } from '@/components/games/HowToPlay';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Screen } from '@/components/ui/Screen';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import {
  useChallenges,
  useMyChallengePoints,
  useSubmitChallengePick,
} from '@/features/fantasy/challenges';
import { useAuth } from '@/lib/auth/AuthProvider';
import type { ChallengeWithState } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { localizeTeamName } from '@/lib/i18n/footballNames';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

function ChallengeCard({
  challenge,
  onPick,
  pending,
}: {
  challenge: ChallengeWithState;
  onPick: (optionId: string) => void;
  pending: boolean;
}) {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  const { userId } = useAuth();
  const [now] = useState(() => Date.now());
  const locked =
    challenge.status !== 'open' || new Date(challenge.lockAt).getTime() <= now;
  const resolved = challenge.status === 'resolved';
  const showTally = locked || challenge.myOption != null;

  // Daily per-match challenge: render a localized prompt + team names instead of
  // the stored English fallback, and show the two crests as a chic header.
  const isMatch =
    !!challenge.resolverKind &&
    challenge.resolverKind.startsWith('match_') &&
    !!challenge.home &&
    !!challenge.away;
  const homeName = challenge.home ? localizeTeamName({ name: challenge.home.name }, locale) : '';
  const awayName = challenge.away ? localizeTeamName({ name: challenge.away.name }, locale) : '';
  const displayPrompt = !isMatch
    ? challenge.prompt
    : challenge.resolverKind === 'match_result'
      ? t('challenges.q.result')
      : challenge.resolverKind === 'match_over_under'
        ? t('challenges.q.overUnder')
        : t('challenges.q.btts');
  const labelFor = (id: string, fallback: string): string => {
    if (!isMatch) return fallback;
    switch (id) {
      case 'home':
        return homeName;
      case 'away':
        return awayName;
      case 'draw':
        return t('challenges.opt.draw');
      case 'over':
        return t('challenges.opt.over');
      case 'under':
        return t('challenges.opt.under');
      case 'yes':
        return t('challenges.opt.yes');
      case 'no':
        return t('challenges.opt.no');
      default:
        return fallback;
    }
  };

  const statusLabel = resolved
    ? challenge.myPoints != null
      ? challenge.myPoints > 0
        ? t('challenges.won', { points: formatNumber(challenge.myPoints, numeralOptions) })
        : t('challenges.missed')
      : t('challenges.resolved')
    : locked
      ? t('challenges.locked')
      : challenge.myOption
        ? `${t('challenges.yourPick')} ✓`
        : t('challenges.open');

  return (
    <Card className="mx-4 gap-3 px-4 py-4">
      <View className="flex-row items-center justify-between">
        <View className="flex-row items-center gap-1.5 rounded-full border border-volt-line bg-volt-glass px-2.5 py-1">
          <Ionicons name="flash" size={12} color={palette.voltBright} />
          <AppText variant="micro" weight="bold" className="text-volt-bright">
            +{formatNumber(challenge.points, numeralOptions)} {t('common.pts')}
          </AppText>
        </View>
        <AppText variant="micro" weight="bold" className="text-soft">
          {statusLabel}
        </AppText>
      </View>

      {isMatch ? (
        <View className="flex-row items-center justify-center gap-2">
          <TeamCrest uri={challenge.home?.logo ?? null} name={homeName} size="sm" />
          <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
            {homeName}
          </AppText>
          <AppText variant="micro" weight="bold" className="px-1 text-muted">
            {t('challenges.vs')}
          </AppText>
          <AppText variant="caption" weight="semibold" className="text-ink" numberOfLines={1}>
            {awayName}
          </AppText>
          <TeamCrest uri={challenge.away?.logo ?? null} name={awayName} size="sm" />
        </View>
      ) : null}

      <AppText variant="body" weight="bold" className="text-ink">
        {displayPrompt}
      </AppText>

      <View className="gap-2">
        {challenge.options.map((option) => {
          const mine = challenge.myOption === option.id;
          const isCorrect = resolved && challenge.correctOption === option.id;
          const votes = challenge.tally[option.id] ?? 0;
          const pct = challenge.totalVotes > 0 ? Math.round((votes / challenge.totalVotes) * 100) : 0;
          const tone = isCorrect ? palette.win : mine ? palette.volt : palette.muted;

          return (
            <Pressable
              key={option.id}
              accessibilityRole="button"
              accessibilityState={{ selected: mine, disabled: locked }}
              disabled={locked || pending}
              onPress={() => onPick(option.id)}
              className="overflow-hidden rounded-card border"
              style={{
                borderColor: isCorrect ? palette.win : mine ? palette.volt : palette.line,
                backgroundColor: palette.inset,
              }}
            >
              {/* tally fill behind the label */}
              {showTally ? (
                <View
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${Math.max(pct, 2)}%`,
                    backgroundColor: `${tone}22`,
                  }}
                />
              ) : null}
              <View className="flex-row items-center gap-2 px-3.5 py-3">
                {mine ? (
                  <Ionicons
                    name={isCorrect ? 'checkmark-circle' : 'radio-button-on'}
                    size={15}
                    color={isCorrect ? palette.win : palette.volt}
                  />
                ) : isCorrect ? (
                  <Ionicons name="checkmark-circle" size={15} color={palette.win} />
                ) : null}
                <AppText
                  variant="caption"
                  weight={mine || isCorrect ? 'bold' : 'medium'}
                  className="flex-1 text-ink"
                  numberOfLines={1}
                >
                  {labelFor(option.id, option.label)}
                </AppText>
                {showTally ? (
                  <AppText variant="micro" weight="bold" tabular style={{ color: tone }}>
                    {formatNumber(pct, numeralOptions)}%
                  </AppText>
                ) : null}
              </View>
            </Pressable>
          );
        })}
      </View>

      {showTally ? (
        <AppText variant="micro" className="text-muted">
          {t('challenges.voteCount', { count: challenge.totalVotes })}
        </AppText>
      ) : !userId ? (
        <AppText variant="micro" className="text-soft">
          {t('predictions.signInToPlay')}
        </AppText>
      ) : null}
    </Card>
  );
}

export default function ChallengesScreen() {
  const palette = useThemePalette();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const { userId } = useAuth();
  const toast = useToast();
  const howto = useHowToPlay('challenges');
  const challengesQuery = useChallenges();
  const pointsQuery = useMyChallengePoints();
  const submit = useSubmitChallengePick();
  const [busyId, setBusyId] = useState<string | null>(null);

  const onPick = (challengeId: string, optionId: string) => {
    setBusyId(challengeId);
    submit.mutate(
      { challengeId, optionId },
      {
        onSettled: () => setBusyId(null),
        onError: (error) => toast.show({ message: t(errorKey(error)), tone: 'error' }),
      },
    );
  };

  return (
    <Screen>
      <HowToPlay visible={howto.visible} onClose={howto.close} i18nKey="challenges" icon="flash" />
      <BackHeader title={t('challenges.title')} right={<HowToPlayButton onPress={howto.open} />} />
      <ScrollView contentContainerStyle={{ paddingBottom: 32, gap: 12 }}>
        <View className="mx-4 mb-1 flex-row items-center gap-3 rounded-card border border-volt-line bg-volt-glass px-4 py-3">
          <Ionicons name="flash" size={20} color={palette.voltBright} />
          <View className="flex-1">
            <AppText variant="caption" weight="bold" className="text-ink">
              {t('challenges.subtitle')}
            </AppText>
            <AppText variant="micro" className="text-soft">
              {t('challenges.howItWorks')}
            </AppText>
          </View>
          {userId ? (
            <View className="items-center">
              <AppText variant="heading" weight="bold" tabular className="text-volt-bright">
                {formatNumber(pointsQuery.data ?? 0, numeralOptions)}
              </AppText>
              <AppText variant="micro" className="text-muted">
                {t('common.pts')}
              </AppText>
            </View>
          ) : null}
        </View>

        {challengesQuery.isPending ? (
          <View className="gap-3 px-4">
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </View>
        ) : challengesQuery.isError ? (
          <ErrorState error={challengesQuery.error} onRetry={() => challengesQuery.refetch()} />
        ) : (challengesQuery.data ?? []).length === 0 ? (
          <EmptyState
            icon="flash-outline"
            title={t('challenges.emptyTitle')}
            body={t('challenges.emptyBody')}
          />
        ) : (
          (challengesQuery.data ?? []).map((challenge) => (
            <ChallengeCard
              key={challenge.id}
              challenge={challenge}
              pending={busyId === challenge.id}
              onPick={(optionId) => onPick(challenge.id, optionId)}
            />
          ))
        )}
      </ScrollView>
    </Screen>
  );
}
